// Turns the site's roles and education into bars on a year axis. Dates are read
// from the same content files as everything else; entries without dates are
// returned separately instead of being guessed.
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Start of a period as a fractional year. "Aug 2024" -> 2024.58, "2023" -> 2023.
export function parseStart(text) {
    const value = String(text || '').trim();
    const monthYear = value.match(/^([a-z]{3})[a-z]*\.?\s+(\d{4})$/i);
    if (monthYear) {
        const month = MONTHS.indexOf(monthYear[1].toLowerCase());
        return month >= 0 ? Number(monthYear[2]) + month / 12 : null;
    }
    return /^\d{4}$/.test(value) ? Number(value) : null;
}

// End of a period: the end of that month or year, or `now` for "Present".
export function parseEnd(text, now) {
    const value = String(text || '').trim();
    if (/^present$/i.test(value)) return now;
    const monthYear = value.match(/^([a-z]{3})[a-z]*\.?\s+(\d{4})$/i);
    if (monthYear) {
        const month = MONTHS.indexOf(monthYear[1].toLowerCase());
        return month >= 0 ? Number(monthYear[2]) + (month + 1) / 12 : null;
    }
    return /^\d{4}$/.test(value) ? Number(value) + 1 : null;
}

export const fractionalYear = (date = new Date()) => {
    const start = Date.UTC(date.getUTCFullYear(), 0, 1);
    const end = Date.UTC(date.getUTCFullYear() + 1, 0, 1);
    return date.getUTCFullYear() + (date.getTime() - start) / (end - start);
};

const label = (item) => [item.startYear, item.endYear].filter(Boolean).join(' – ');

export function buildTimeline({ experience = [], education = [], now = fractionalYear() }) {
    const entries = [
        ...experience.map((job) => ({
            id: `role-${job.id}`, kind: 'Role', title: job.company, sub: job.jobtitle,
            period: label(job), start: parseStart(job.startYear), end: parseEnd(job.endYear, now),
        })),
        ...education.map((item) => ({
            id: `edu-${item.id}`, kind: 'Education', title: item.institution, sub: item.course,
            period: label(item), start: parseStart(item.startYear), end: parseEnd(item.endYear, now),
        })),
    ];

    const dated = entries.filter((e) => e.start !== null && e.end !== null && e.end > e.start);
    const undated = entries.filter((e) => !dated.includes(e));
    if (dated.length === 0) return { items: [], undated, from: 0, to: 0, lanes: 0, ticks: [] };

    // Earliest first; each bar goes in the first lane where it does not overlap.
    const laneEnds = [];
    const items = [...dated]
        .sort((a, b) => a.start - b.start || a.end - b.end)
        .map((entry) => {
            let lane = laneEnds.findIndex((end) => end <= entry.start);
            if (lane === -1) lane = laneEnds.length;
            laneEnds[lane] = entry.end;
            return { ...entry, lane };
        });

    const from = Math.floor(Math.min(...items.map((i) => i.start)));
    const to = Math.ceil(Math.max(...items.map((i) => i.end)));
    const ticks = Array.from({ length: to - from + 1 }, (_, index) => from + index);
    return { items, undated, from, to, lanes: laneEnds.length, ticks };
}
